import jsPDF from 'jspdf';
import { renderFacilityInformation } from '../../../../../../../utils/pdfHelpers';

export const generateProductInvoicePDF = async (invoiceData, patientInfo = null, officeData = null) => {
    const pdf = new jsPDF('p', 'mm', 'letter');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const lineHeight = 7;
    let yPosition = margin;

    // Header
    pdf.setFontSize(24);
    pdf.setTextColor(41, 128, 185);
    pdf.text('PRODUCT INVOICE', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += lineHeight * 2.5;

    // Two-column layout for Facility and Customer Information
    const leftColumnX = margin;
    const rightColumnX = pageWidth / 2 + 10;
    const columnWidth = (pageWidth - margin * 2 - 20) / 2;
    const sectionStartY = yPosition;

    // Facility Information (Left Column)
    const facilityEndY = renderFacilityInformation(pdf, officeData, {
        startX: leftColumnX,
        startY: yPosition,
        lineHeight,
        columnWidth,
    });
    yPosition = Math.max(yPosition, facilityEndY);

    // Customer Information (Right Column)
    if (patientInfo) {
        let customerY = sectionStartY;

        pdf.setFontSize(12);
        pdf.setTextColor(52, 73, 94);
        pdf.text('Customer Information', rightColumnX, customerY);
        customerY += lineHeight;

        pdf.setFontSize(9);
        pdf.setTextColor(0, 0, 0);

        // Customer Name
        const customerName = patientInfo.first_name && patientInfo.last_name
            ? `${patientInfo.first_name} ${patientInfo.middle_name ? patientInfo.middle_name + ' ' : ''}${patientInfo.last_name}`
            : 'N/A';
        pdf.text(`Name: ${customerName}`, rightColumnX, customerY);
        customerY += lineHeight * 0.8;

        // Date of Birth and Age
        if (patientInfo.date_of_birth) {
            const dob = new Date(patientInfo.date_of_birth).toLocaleDateString();
            pdf.text(`DOB: ${dob}`, rightColumnX, customerY);
            customerY += lineHeight * 0.8;

            // Calculate age
            const today = new Date();
            const birthDate = new Date(patientInfo.date_of_birth);
            const age = Math.floor((today - birthDate) / (365.25 * 24 * 60 * 60 * 1000));
            pdf.text(`Age: ${age} y/o`, rightColumnX, customerY);
            customerY += lineHeight * 0.8;
        }

        // Gender
        if (patientInfo.gender) {
            pdf.text(`Gender: ${patientInfo.gender}`, rightColumnX, customerY);
            customerY += lineHeight * 0.8;
        }

        // Customer ID
        if (patientInfo.id) {
            pdf.text(`Customer ID: ${patientInfo.id}`, rightColumnX, customerY);
            customerY += lineHeight * 0.8;
        }

        // Address (using current address fields)
        const addressParts = [];
        if (patientInfo.current_address_line_1) addressParts.push(patientInfo.current_address_line_1);
        if (patientInfo.current_address_line_2) addressParts.push(patientInfo.current_address_line_2);
        if (patientInfo.current_city) addressParts.push(patientInfo.current_city);
        if (patientInfo.current_state) addressParts.push(patientInfo.current_state);
        if (patientInfo.current_country) addressParts.push(patientInfo.current_country);
        if (patientInfo.current_zipcode) addressParts.push(patientInfo.current_zipcode);

        if (addressParts.length > 0) {
            const addressLines = pdf.splitTextToSize(`Address: ${addressParts.join(', ')}`, columnWidth);
            addressLines.forEach(line => {
                pdf.text(line, rightColumnX, customerY);
                customerY += lineHeight * 0.8;
            });
        }

        // Phone
        if (patientInfo.patient_phone) {
            pdf.text(`Phone: ${patientInfo.patient_phone}`, rightColumnX, customerY);
            customerY += lineHeight * 0.8;
        }

        // Email
        if (patientInfo.patient_email) {
            pdf.text(`Email: ${patientInfo.patient_email}`, rightColumnX, customerY);
            customerY += lineHeight * 0.8;
        }

        // Add invoice information under customer information
        if (invoiceData) {
            customerY += lineHeight * 0.5;

            // Draw a subtle separator
            pdf.setDrawColor(220, 220, 220);
            pdf.line(rightColumnX, customerY, rightColumnX + columnWidth, customerY);
            customerY += lineHeight;

            pdf.setFontSize(10);
            pdf.setTextColor(52, 73, 94);
            pdf.text('Invoice Information', rightColumnX, customerY);
            customerY += lineHeight;

            pdf.setFontSize(9);
            pdf.setTextColor(0, 0, 0);

            // Invoice ID and Status
            if (invoiceData.invoice_id) {
                pdf.text(`Invoice ID: ${invoiceData.invoice_id}`, rightColumnX, customerY);
                customerY += lineHeight * 0.8;
            }

            if (invoiceData.status) {
                const statusText = invoiceData.status.charAt(0).toUpperCase() + invoiceData.status.slice(1);
                pdf.text(`Status: ${statusText}`, rightColumnX, customerY);
                customerY += lineHeight * 0.8;
            }

            // Issue Date
            if (invoiceData.issue_date) {
                const issueDate = new Date(invoiceData.issue_date).toLocaleDateString();
                const issueTime = new Date(invoiceData.issue_date).toLocaleTimeString();
                pdf.text(`Issue Date: ${issueDate} ${issueTime}`, rightColumnX, customerY);
                customerY += lineHeight * 0.8;
            }

            // Created By
            if (invoiceData.created_by || invoiceData.added_by) {
                pdf.text(`Created By: ${invoiceData.created_by || invoiceData.added_by}`, rightColumnX, customerY);
                customerY += lineHeight * 0.8;
            }

            // Payment Method
            if (invoiceData.payment_method) {
                pdf.text(`Payment Method: ${invoiceData.payment_method}`, rightColumnX, customerY);
                customerY += lineHeight * 0.8;
            }
        }

        yPosition = Math.max(yPosition, customerY);
    }

    yPosition += lineHeight * 1.5;

    // Draw separator line
    pdf.setDrawColor(52, 73, 94);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += lineHeight * 1.5;

    // Products Section
    if (invoiceData) {
        // Check if we need a new page
        if (yPosition > pageHeight - 90) {
            pdf.addPage();
            yPosition = margin;
        }

        // Determine currency for the entire invoice - use office settings if available, fallback to invoice data or default
        const currency = officeData?.local_currency || invoiceData.currency || 'USD';
        const currencySymbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'PKR' ? 'PKR ' : currency === 'GBP' ? '£' : currency === 'JPY' ? '¥' : currency === 'CAD' ? 'C$' : currency === 'AUD' ? 'A$' : `${currency} `;

        // Products/Sale Items
        pdf.setFontSize(12);
        pdf.setTextColor(52, 73, 94);
        pdf.text('Sale Items:', margin, yPosition);
        yPosition += lineHeight;

        // Handle product_details array
        const products = invoiceData.product_details || [];
        let totalAmount = 0;

        if (products.length > 0) {
            // Define table column positions for better layout
            const tableStartX = margin;
            const colPositions = {
                number: tableStartX + 5,
                item: tableStartX + 20,
                qty: tableStartX + 85,
                unitPrice: tableStartX + 110,
                totalPrice: tableStartX + 150
            };

            // Table header
            pdf.setFontSize(10);
            pdf.setTextColor(255, 255, 255);
            pdf.setFillColor(52, 73, 94);
            pdf.rect(margin, yPosition, pageWidth - margin * 2, lineHeight + 2, 'F');

            pdf.text('#', colPositions.number, yPosition + lineHeight * 0.7);
            pdf.text('Sale Item', colPositions.item, yPosition + lineHeight * 0.7);
            pdf.text('QTY', colPositions.qty, yPosition + lineHeight * 0.7);
            pdf.text('Unit Price', colPositions.unitPrice, yPosition + lineHeight * 0.7);
            pdf.text('Total Price', colPositions.totalPrice, yPosition + lineHeight * 0.7);
            yPosition += lineHeight + 2;

            pdf.setTextColor(0, 0, 0);

            products.forEach((product, productIndex) => {
                // Check if we need a new page
                if (yPosition > pageHeight - 80) {
                    pdf.addPage();
                    yPosition = margin;
                    
                    // Redraw header on new page
                    pdf.setFontSize(10);
                    pdf.setTextColor(255, 255, 255);
                    pdf.setFillColor(52, 73, 94);
                    pdf.rect(margin, yPosition, pageWidth - margin * 2, lineHeight + 2, 'F');

                    pdf.text('#', colPositions.number, yPosition + lineHeight * 0.7);
                    pdf.text('Sale Item', colPositions.item, yPosition + lineHeight * 0.7);
                    pdf.text('QTY', colPositions.qty, yPosition + lineHeight * 0.7);
                    pdf.text('Unit Price', colPositions.unitPrice, yPosition + lineHeight * 0.7);
                    pdf.text('Total Price', colPositions.totalPrice, yPosition + lineHeight * 0.7);
                    yPosition += lineHeight + 2;
                    
                    pdf.setTextColor(0, 0, 0);
                }

                // Calculate row height needed for this product
                const productName = product.items || 'Unknown Item';
                const maxItemWidth = colPositions.qty - colPositions.item - 10;
                const productNameLines = pdf.splitTextToSize(productName, maxItemWidth);
                const rowHeight = Math.max(lineHeight + 2, productNameLines.length * lineHeight);

                // Alternate row colors for better readability
                if (productIndex % 2 === 1) {
                    pdf.setFillColor(248, 249, 250);
                    pdf.rect(margin, yPosition, pageWidth - margin * 2, rowHeight, 'F');
                }

                // Row data
                pdf.setFontSize(9);
                pdf.setTextColor(0, 0, 0);
                
                // Row number
                pdf.text(`${productIndex + 1}.`, colPositions.number, yPosition + lineHeight * 0.7);

                // Product name (can be multiple lines)
                productNameLines.forEach((line, lineIndex) => {
                    pdf.text(line, colPositions.item, yPosition + lineHeight * 0.7 + (lineIndex * lineHeight));
                });

                // Quantity (centered in column)
                const quantity = product.quantity || 1;
                pdf.text(quantity.toString(), colPositions.qty + 5, yPosition + lineHeight * 0.7);

                // Unit Price
                const unitPrice = parseFloat(product.unit_price) || 0;
                pdf.text(`${currencySymbol}${unitPrice.toFixed(2)}`, colPositions.unitPrice, yPosition + lineHeight * 0.7);

                // Total Price for this item
                const itemTotal = parseFloat(product.price) || (unitPrice * quantity);
                totalAmount += itemTotal;
                pdf.text(`${currencySymbol}${itemTotal.toFixed(2)}`, colPositions.totalPrice, yPosition + lineHeight * 0.7);

                yPosition += rowHeight + 1; // Add small spacing between rows
            });

            // Invoice Summary Section
            yPosition += lineHeight * 1.5;
            pdf.setDrawColor(220, 220, 220);
            pdf.line(margin, yPosition, pageWidth - margin, yPosition);
            yPosition += lineHeight * 1.5;

            // Use actual invoice data values
            const subtotal = invoiceData.price ? parseFloat(invoiceData.price) : totalAmount;
            const discountPercentage = invoiceData.discount ? parseFloat(invoiceData.discount) : null;
            const discountAmount = invoiceData.discounted_amount ? parseFloat(invoiceData.discounted_amount) : null;
            const taxAmount = invoiceData.tax ? parseFloat(invoiceData.tax) : null;
            const grandTotal = invoiceData.total_price ? parseFloat(invoiceData.total_price) : subtotal;

            // Define summary layout positions
            const summaryLabelX = pageWidth - margin - 120;
            const summaryValueX = pageWidth - margin - 10;

            pdf.setFontSize(11);
            pdf.setTextColor(0, 0, 0);

            // Sub Total
            pdf.text('Sub Total:', summaryLabelX, yPosition);
            pdf.text(`${currencySymbol}${subtotal.toFixed(2)}`, summaryValueX, yPosition, { align: 'right' });
            yPosition += lineHeight * 1.2;

            // Discount (only show if there's actual discount data)
            if (discountAmount && discountAmount > 0) {
                pdf.setTextColor(220, 53, 69); // Red color for discount
                const discountText = discountPercentage ? `Discount ${discountPercentage}%:` : 'Discount:';
                pdf.text(discountText, summaryLabelX, yPosition);
                pdf.text(`${currencySymbol}${discountAmount.toFixed(2)}`, summaryValueX, yPosition, { align: 'right' });
                pdf.setTextColor(0, 0, 0); // Reset color
                yPosition += lineHeight * 1.2;
            }

            // Tax (only show if there's actual tax data)
            if (taxAmount && taxAmount > 0) {
                pdf.text('Tax:', summaryLabelX, yPosition);
                pdf.text(`${currencySymbol}${taxAmount.toFixed(2)}`, summaryValueX, yPosition, { align: 'right' });
                yPosition += lineHeight * 1.2;
            }

            // Draw line before grand total
            yPosition += lineHeight * 0.5;
            pdf.setDrawColor(52, 73, 94);
            pdf.setLineWidth(0.5);
            pdf.line(summaryLabelX, yPosition, summaryValueX, yPosition);
            yPosition += lineHeight * 1.2;

            // Grand Total
            pdf.setFontSize(14);
            pdf.setTextColor(52, 73, 94);
            pdf.text('Grand Total:', summaryLabelX, yPosition);
            pdf.text(`${currencySymbol}${grandTotal.toFixed(2)}`, summaryValueX, yPosition, { align: 'right' });
            yPosition += lineHeight * 2;

        } else {
            pdf.setFontSize(9);
            pdf.setTextColor(128, 128, 128);
            pdf.text('No sale items specified', margin + 5, yPosition);
            yPosition += lineHeight;

            // Display detailed breakdown even without product details if total price is available
            if (invoiceData.price) {
                yPosition += lineHeight;

                // Draw separator line
                pdf.setDrawColor(220, 220, 220);
                pdf.line(margin, yPosition, pageWidth - margin, yPosition);
                yPosition += lineHeight;

                // Use actual invoice data values
                const subtotal = invoiceData.price ? parseFloat(invoiceData.price) : 0;
                const discountPercentage = invoiceData.discount ? parseFloat(invoiceData.discount) : null;
                const discountAmount = invoiceData.discounted_amount ? parseFloat(invoiceData.discounted_amount) : null;
                const taxAmount = invoiceData.tax ? parseFloat(invoiceData.tax) : null;
                const grandTotal = invoiceData.total_price ? parseFloat(invoiceData.total_price) : subtotal;

                pdf.setFontSize(10);
                pdf.setTextColor(0, 0, 0);

                // Sub Total
                pdf.text('Sub Total:', pageWidth - margin - 100, yPosition);
                pdf.text(`${currencySymbol}${subtotal.toFixed(2)}`, pageWidth - margin - 30, yPosition, { align: 'right' });
                yPosition += lineHeight;

                // Discount (only show if there's actual discount data)
                if (discountAmount && discountAmount > 0) {
                    pdf.setTextColor(220, 53, 69);
                    const discountText = discountPercentage ? `Discount ${discountPercentage}%:` : 'Discount:';
                    pdf.text(discountText, pageWidth - margin - 100, yPosition);
                    pdf.text(`${currencySymbol}${discountAmount.toFixed(2)}`, pageWidth - margin - 30, yPosition, { align: 'right' });
                    pdf.setTextColor(0, 0, 0); // Reset color
                    yPosition += lineHeight;
                }

                // Tax (only show if there's actual tax data)
                if (taxAmount && taxAmount > 0) {
                    pdf.text('Tax:', pageWidth - margin - 100, yPosition);
                    pdf.text(`${currencySymbol}${taxAmount.toFixed(2)}`, pageWidth - margin - 30, yPosition, { align: 'right' });
                    yPosition += lineHeight;
                }

                // Draw line before grand total
                pdf.setDrawColor(52, 73, 94);
                pdf.line(pageWidth - margin - 120, yPosition, pageWidth - margin, yPosition);
                yPosition += lineHeight;

                // Grand Total
                pdf.setFontSize(12);
                pdf.setTextColor(52, 73, 94);
                pdf.text('Grand Total:', pageWidth - margin - 100, yPosition);
                pdf.text(`${currencySymbol}${grandTotal.toFixed(2)}`, pageWidth - margin - 30, yPosition, { align: 'right' });
                yPosition += lineHeight;
            }
        }
    }

    // Add page numbers
    const pageCount = pdf.internal.pages.length - 1;
    for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
    }

    // Save the PDF
    const dateStamp = new Date().toISOString().split('T')[0];
    const invoiceId = invoiceData?.invoice_id || 'Unknown';
    const patientName = patientInfo?.first_name && patientInfo?.last_name
        ? `${patientInfo.first_name}_${patientInfo.last_name}`
        : 'Customer';
    const fileName = `Product_Invoice_${invoiceId}_${patientName}_${dateStamp}.pdf`;

    pdf.save(fileName);
};
